/**
 * Client dell'API CV RAG.
 *
 * EventSource non e' utilizzabile: supporta solo GET e qui serve un POST con la
 * cronologia. Lo streaming SSE viene quindi letto a mano dal ReadableStream
 * della fetch, che ha il vantaggio di supportare AbortController.
 */

import { getClientId } from './clientId';
import { ChatError, ChatMeta, StreamChatOptions, StreamEvent } from './chatTypes';
import { isMockMode, mockFetchMeta, mockStreamChat, mockWarmup } from './mockTransport';

export * from './chatTypes';
export { isMockMode } from './mockTransport';

const API_BASE = (process.env.REACT_APP_CV_CHAT_API ?? '').replace(/\/$/, '');

/** true se esiste un endpoint verso cui parlare. */
export const isConfigured = (): boolean => API_BASE.length > 0;

/**
 * Se mostrare i punti di ingresso (fumetto sul ritratto, icona in navbar).
 *
 * In produzione servono solo con un backend configurato: un pulsante che porta a
 * una pagina non funzionante e' peggio dell'assenza del pulsante.
 *
 * Fuori dalla produzione restano visibili anche senza endpoint, altrimenti la
 * UI della chat non e' sviluppabile ne' testabile finche' l'infrastruttura non
 * esiste: `yarn start` non legge .env.production e senza questa distinzione
 * l'interfaccia risulta semplicemente assente. La pagina /talktome mostra in
 * quel caso il proprio stato "non configurato".
 */
export const shouldShowEntryPoints = (): boolean =>
  isConfigured() || process.env.NODE_ENV !== 'production';

/**
 * Header con l'ID del client, usato dal backend come chiave di rate limiting
 * assieme all'IP. Vedi clientId.ts per la scelta di non usare un fingerprint
 * hardware.
 */
const clientIdHeader = (): Record<string, string> => ({ 'x-client-id': getClientId() });

/**
 * Sveglia il container prima che l'utente scriva.
 *
 * Con minReplicas = 0 la prima richiesta paga l'avvio a freddo. Chiamare questo
 * endpoint all'apertura della chat sposta quell'attesa in un momento in cui
 * l'utente non la percepisce, e non costa nulla: /api/health non tocca Azure OpenAI.
 */
export async function warmup(signal?: AbortSignal): Promise<boolean> {
  if (isMockMode()) return mockWarmup(signal);
  if (!isConfigured()) return false;
  try {
    // /api/health non consuma rate limit: altrimenti il warmup brucerebbe
    // l'unica chiamata disponibile nel minuto.
    const response = await fetch(`${API_BASE}/api/health`, { method: 'GET', signal });
    return response.ok;
  } catch {
    return false;
  }
}

export async function fetchMeta(signal?: AbortSignal): Promise<ChatMeta | null> {
  if (isMockMode()) return mockFetchMeta(signal);
  if (!isConfigured()) return null;
  try {
    const response = await fetch(`${API_BASE}/api/meta`, { method: 'GET', signal });
    if (!response.ok) return null;
    return (await response.json()) as ChatMeta;
  } catch {
    return null;
  }
}

/** Invia la conversazione e inoltra ogni evento SSE a onEvent. */
export async function streamChat(options: StreamChatOptions): Promise<void> {
  // In sviluppo senza backend risponde il transport simulato: la chat resta
  // usabile end-to-end prima che l'infrastruttura esista.
  if (isMockMode()) return mockStreamChat(options);

  const { messages, persona, signal, onEvent } = options;

  if (!isConfigured()) {
    throw new ChatError('Endpoint non configurato: impostare REACT_APP_CV_CHAT_API.', 'server');
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/chat`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
        ...clientIdHeader(),
      },
      body: JSON.stringify({ messages, persona }),
      signal,
    });
  } catch {
    if (signal.aborted) throw new ChatError('Richiesta annullata.', 'aborted');
    throw new ChatError('Server non raggiungibile.', 'network');
  }

  if (response.status === 429) {
    const payload = await response.json().catch(() => null);
    // FastAPI annida il corpo di HTTPException sotto "detail".
    const detail = payload?.detail ?? payload;
    const headerValue = Number(response.headers.get('retry-after'));
    const retryAfter =
      Number(detail?.retry_after) || (Number.isFinite(headerValue) ? headerValue : 0) || 60;

    throw new ChatError(
      detail?.message ?? 'Troppi messaggi: riprova tra qualche istante.',
      'rate_limited',
      retryAfter
    );
  }

  if (response.status === 403) {
    throw new ChatError('Richiesta non autorizzata da questa origine.', 'forbidden');
  }

  if (!response.ok || !response.body) {
    throw new ChatError(`Errore del server (${response.status}).`, 'server');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Gli eventi SSE sono separati da una riga vuota.
      let separator = buffer.indexOf('\n\n');
      while (separator !== -1) {
        const rawEvent = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);

        const dataLine = rawEvent.split('\n').find((line) => line.startsWith('data:'));

        if (dataLine) {
          const json = dataLine.slice(5).trim();
          if (json) {
            try {
              onEvent(JSON.parse(json) as StreamEvent);
            } catch {
              // Un frammento illeggibile non deve interrompere lo stream.
            }
          }
        }
        separator = buffer.indexOf('\n\n');
      }
    }
  } catch {
    if (signal.aborted) throw new ChatError('Richiesta annullata.', 'aborted');
    throw new ChatError('Stream interrotto.', 'network');
  } finally {
    reader.releaseLock();
  }
}
